/**
 * Parse GitHub URLs to extract owner, repo, branch, and file path
 * Supports both simple repo URLs and full file URLs
 */

/**
 * Parse GitHub URL - handles both repo and file URLs
 * Examples:
 * - https://github.com/owner/repo
 * - https://github.com/owner/repo.git
 * - https://github.com/owner/repo/blob/main/path/to/file.TcPOU
 * - https://github.com/owner/repo/blob/feature/branch/path/to/file.TcPOU
 * 
 * @param {string} url - GitHub URL
 * @returns {Object} - { ownerRepo, branch, filePath } or null if invalid
 */
export const parseGithubUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  try {
    const urlObj = new URL(url);
    
    // Only accept github.com URLs
    if (!urlObj.hostname.includes('github.com')) return null;

    const pathname = urlObj.pathname;
    const isTrailingSlash = pathname.endsWith('/');
    const parts = pathname.split('/').filter(p => p); // Remove empty strings

    // Need at least owner/repo
    if (parts.length < 2) return null;

    const owner = parts[0];
    const repo = parts[1].replace(/\.git$/, ''); // Remove .git if present

    // Check if it's a file or folder URL (has /blob/branch/path or /tree/branch/path)
    const typeIndicator = parts[2];
    if (parts.length > 2 && (typeIndicator === 'blob' || typeIndicator === 'tree')) {
      if (parts.length < 4) return null; // Need at least /type/branch

      const typeIndex = parts.indexOf(typeIndicator);
      const branchStart = typeIndex + 1;

      if (branchStart >= parts.length) {
        // Only /type/branch with no file path
        const branch = parts[branchStart];
        return {
          ownerRepo: `/${owner}/${repo}`,
          branch,
          filePath: null,
          type: typeIndicator === 'blob' ? 'blob' : 'tree'
        };
      }

      // Find where branch ends and file path begins
      const branch = parts[branchStart];
      const filePathParts = parts.slice(branchStart + 1);
      const filePath = filePathParts.join('/');

      // Determine actual type: if URL ends in / or last part has no extension, it's likely a tree
      let actualType = typeIndicator === 'blob' ? 'blob' : 'tree';
      if (actualType === 'blob' && filePath) {
        const lastPart = filePathParts[filePathParts.length - 1];
        if (isTrailingSlash || !lastPart.includes('.')) {
          actualType = 'tree';
        }
      }

      return {
        ownerRepo: `/${owner}/${repo}`,
        branch,
        filePath: filePath || null,
        type: actualType
      };
    }

    // Simple repo URL
    return {
      ownerRepo: `/${owner}/${repo}`,
      branch: null,
      filePath: null,
      type: 'repo'
    };
  } catch (error) {
    return null;
  }
};

/**
 * Validate if URL is a valid GitHub URL (repo or file)
 * @param {string} url - URL to validate
 * @returns {boolean}
 */
export const isValidGithubFileUrl = (url) => {
  return parseGithubUrl(url) !== null;
};
